<template>
  <div class="fts-verification-code" :class="{ 'fts-verification-code--compact': compact }">
    <label class="fts-verification-code__label" :for="inputId">Verification code</label>

    <!-- One real input (mobile keyboards, one-time-code autofill, paste) drawn as six cells. -->
    <div class="fts-verification-code__cells"
      :class="{ 'is-error': !!error, 'is-verified': verified, 'is-disabled': busy }"
      @click="focus">
      <input
        ref="input"
        :id="inputId"
        class="fts-verification-code__input"
        type="text"
        inputmode="numeric"
        pattern="[0-9]*"
        maxlength="6"
        autocomplete="one-time-code"
        aria-label="6-digit verification code"
        :aria-describedby="statusId"
        :aria-invalid="!!error"
        :value="code"
        :disabled="busy || verified"
        @input="onInput"
        @paste.prevent="onPaste"
        @keydown.enter.prevent="submit"
        @focus="focused = true"
        @blur="focused = false"
      />
      <span
        v-for="i in 6"
        :key="i"
        class="fts-verification-code__cell"
        :class="{ 'is-focus': focused && !verified && i - 1 === Math.min(code.length, 5) }"
        aria-hidden="true">{{ code[i - 1] || '' }}</span>
    </div>

    <div class="fts-verification-code__status" :id="statusId" aria-live="polite">
      <span v-if="verified" class="is-verified">Verified</span>
      <span v-else-if="error" class="is-error">{{ error }}</span>
      <span v-else-if="expired" class="is-error">That code expired. Request a new one.</span>
      <span v-else>Expires in {{ expiresIn }}</span>
    </div>

    <fts-btn
      v-if="showButton"
      type="button"
      class="login-form-action fts-verification-code__button"
      color="primary"
      text-transform-init
      box-shadow-none
      :disabled="code.length < 6 || busy || verified"
      :loading="busy"
      @click="submit">
      {{ buttonText }}
    </fts-btn>

    <div class="fts-verification-code__links">
      <div>
        Didn't get it?
        <a v-if="canResend" href="#" class="fts-link-primary" @click.prevent="$emit('resend')">Resend code</a>
        <span v-else-if="sendsLeft === 0" class="fts-verification-code__muted">No more codes can be sent</span>
        <span v-else class="fts-verification-code__muted">Resend code in {{ resendIn }}</span>
      </div>
      <div v-if="allowChangeEmail">
        Wrong address?
        <a href="#" class="fts-link-primary" @click.prevent="$emit('change-email')">Change email</a>
      </div>
      <div v-if="linkHint" class="fts-verification-code__hint">
        Or open the email and press <span class="fts-font-medium">Verify and continue</span> — it finishes this step in the tab it opens.
      </div>
    </div>
  </div>
</template>

<script>
import FTSBtn from "../elements/FTSBtn";

let sequence = 0;

/**
 * The 6-digit code field shared by sign-up (FTSLogin), the workspace-create modal and the
 * onboarding wizard — docs/identity-email-verification-proposal.md, D-3.
 *
 * The parent owns the session: it passes the `verification` view the server returned
 * ({ id, email, expiresAt, resendAfter, attemptsLeft, sendsLeft }) and reacts to `submit(code)`,
 * `resend` and `change-email`. The server is the clock — the countdowns only render its
 * `expiresAt` / `resendAfter`.
 */
export default {
  name: "FTSVerificationCode",
  components: { "fts-btn": FTSBtn },
  props: {
    verification: { type: Object, required: true },
    busy: { type: Boolean, default: false },
    verified: { type: Boolean, default: false },
    error: { type: String, default: null },
    buttonText: { type: String, default: "Verify and continue" },
    showButton: { type: Boolean, default: true },
    allowChangeEmail: { type: Boolean, default: false },
    linkHint: { type: Boolean, default: false },
    compact: { type: Boolean, default: false },
    autofocus: { type: Boolean, default: true },
  },
  data() {
    sequence += 1;
    return {
      code: "",
      focused: false,
      now: Date.now(),
      timer: null,
      submittedFor: null,
      inputId: `fts-verification-code-${sequence}`,
      statusId: `fts-verification-code-status-${sequence}`,
    };
  },
  computed: {
    expiresAt() {
      return this.verification && this.verification.expiresAt ? new Date(this.verification.expiresAt).getTime() : 0;
    },
    resendAt() {
      return this.verification && this.verification.resendAfter ? new Date(this.verification.resendAfter).getTime() : 0;
    },
    sendsLeft() {
      return this.verification && typeof this.verification.sendsLeft === "number" ? this.verification.sendsLeft : null;
    },
    expired() {
      return this.expiresAt > 0 && this.now > this.expiresAt;
    },
    expiresIn() {
      return this.clock(this.expiresAt - this.now);
    },
    resendIn() {
      return this.clock(this.resendAt - this.now);
    },
    canResend() {
      if (this.sendsLeft === 0) return false;
      return this.now >= this.resendAt;
    },
  },
  watch: {
    // A new code (resend) or a new session clears what was typed.
    "verification.id"() { this.reset(); },
    "verification.resendAfter"() { this.reset(); },
    error(value) {
      if (value) this.submittedFor = null;
    },
  },
  mounted() {
    this.timer = setInterval(() => { this.now = Date.now(); }, 1000);
    if (this.autofocus) this.$nextTick(this.focus);
  },
  beforeDestroy() {
    clearInterval(this.timer);
  },
  methods: {
    clock(ms) {
      const total = Math.max(0, Math.ceil(ms / 1000));
      const minutes = Math.floor(total / 60);
      const seconds = total % 60;
      return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
    },
    focus() {
      this.$refs.input && this.$refs.input.focus();
    },
    reset() {
      this.code = "";
      this.submittedFor = null;
      this.$nextTick(this.focus);
    },
    digits(value) {
      return String(value || "").replace(/\D/g, "").slice(0, 6);
    },
    onInput(event) {
      this.setCode(this.digits(event.target.value));
    },
    onPaste(event) {
      const text = event.clipboardData ? event.clipboardData.getData("text") : "";
      this.setCode(this.digits(text));
    },
    setCode(value) {
      this.code = value;
      if (this.$refs.input && this.$refs.input.value !== value) this.$refs.input.value = value;
      this.$emit("input", value);
      // The sixth digit submits — once per value, so a rejected code is not re-sent on re-render.
      if (value.length === 6 && this.submittedFor !== value) {
        this.submit();
      }
    },
    submit() {
      if (this.busy || this.verified || this.code.length < 6) return;
      this.submittedFor = this.code;
      this.$emit("submit", this.code);
    },
  },
};
</script>

<style lang="scss" scoped>
.fts-verification-code {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__label {
    font-size: var(--inistate-label-text-12, 12px);
    font-weight: var(--inistate-font-weight-medium, 500);
    color: var(--inistate-text-secondary-color);
    margin: 0 0 -8px;
  }

  &__cells {
    position: relative;
    display: flex;
    gap: 8px;
    cursor: text;
    width: max-content;
    max-width: 100%;
  }

  // The real control sits over the cells, invisible but hit-testable and focusable.
  &__input {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    color: transparent;
    caret-color: transparent;
    border: 0;
    padding: 0;
    margin: 0;
    font-size: 24px;
    letter-spacing: 2em;
    background: transparent;
    outline: none;
    z-index: 1;
  }

  &__cell {
    width: 48px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--inistate-border-primary, #e0e0e0);
    border-radius: var(--inistate-rounded-4, 4px);
    background: var(--fts-card-background, #fff);
    color: var(--inistate-text-primary-color);
    font-size: 24px;
    font-weight: var(--inistate-font-weight-medium, 500);
    font-variant-numeric: tabular-nums;
    transition: border-color .12s ease;

    // Figma input spec: focus is a 2 px border, no halo.
    &.is-focus {
      border: 2px solid var(--fts-primary);
    }
    &.is-focus::after {
      content: "";
      width: 1px;
      height: 24px;
      background: var(--fts-primary);
      animation: fts-verification-caret 1s steps(2) infinite;
    }
  }

  &__cells.is-error &__cell {
    border: 2px solid var(--fts-danger);
    color: var(--fts-danger);
  }
  &__cells.is-verified &__cell {
    border: 2px solid var(--fts-success, #1e8e5a);
    color: var(--fts-success, #1e8e5a);
  }
  &__cells.is-disabled { opacity: .7; }

  &__status {
    font-size: var(--inistate-label-text-12, 12px);
    color: var(--inistate-text-secondary-color);
    margin-top: -8px;
    min-height: 18px;
    .is-error { color: var(--fts-danger); }
    .is-verified { color: var(--fts-success, #1e8e5a); }
  }

  &__button { width: 100%; }

  &__links {
    display: flex;
    flex-direction: column;
    gap: 8px;
    font-size: var(--inistate-label-text-14, 14px);
    color: var(--inistate-text-primary-color);
  }
  &__muted { color: var(--inistate-text-secondary-color); }
  &__hint {
    font-size: var(--inistate-label-text-12, 12px);
    line-height: 1.5;
    color: var(--inistate-text-secondary-color);
  }

  &--compact &__cell {
    width: 40px;
    height: 48px;
    font-size: 20px;
  }

  @media (max-width: 425px) {
    &__cell { width: 40px; height: 48px; font-size: 20px; }
  }
}

@keyframes fts-verification-caret {
  50% { opacity: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .fts-verification-code__cell.is-focus::after { animation: none; }
}
</style>
